import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Scale, Building2, CalendarDays, Check, Lightbulb, ListChecks, Mic, Phone, Send, Tag, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, shortDate } from '../api';
import { PageHeader } from '../components/PageHeader';
import { OfferCard } from '../components/OfferCard';
import { ErrorState, Loading } from '../components/Loading';
import type { DashboardData, Offer } from '../types';

const taskIcons: Record<string, typeof Phone> = { phone: Phone, calendar: CalendarDays, office: Building2, compare: Scale, note: ListChecks };

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api<DashboardData>('/api/dashboard').then(setData).catch(err => setError(err.message)), []);
  useEffect(() => { load(); }, [load]);
  if (!data && !error) return <Loading />;
  if (!data) return <ErrorState message={error} retry={load} />;
  const toggle = async (id: string) => { await api(`/api/tasks/${id}/toggle`, { method: 'PATCH' }); setData(current => current ? ({ ...current, tasks: current.tasks.filter(task => task.id !== id) }) : current); };
  const toggleFavorite = async (offer: Offer, favorite: boolean) => {
    await api(`/api/offers/${offer.id}/favorite`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ favorite }) });
    setData(current => {
      if (!current) return current;
      const updated = { ...offer, favorite };
      return {
        ...current,
        offers: current.offers.map(item => item.id === offer.id ? updated : item),
        favorites: favorite ? [updated, ...current.favorites.filter(item => item.id !== offer.id)] : current.favorites.filter(item => item.id !== offer.id),
      };
    });
  };
  return <div className="page dashboard-page">
    <PageHeader title={data.project.name} subtitle={data.project.subtitle} />
    <section className="stats-card">
      <div><span className="metric-icon blue"><Tag /></span><p>Aktywne oferty<strong>{data.stats.active}</strong><small>+3 od wczoraj</small></p></div>
      <div><span className="metric-icon green"><TrendingUp /></span><p>Nowe zmiany<strong>{data.stats.changes}</strong><small>Dziś</small></p></div>
      <div><span className="metric-icon orange"><Lightbulb /></span><p>Wgląd<strong>{data.stats.attention}</strong><small>Wymaga uwagi</small></p></div>
    </section>
    <section className="section-block tasks-block">
      <div className="section-heading"><h2>Najbliższe kroki</h2><button>Widok listy⌄</button></div>
      <div className="task-list">{data.tasks.map(task => {
        const Icon = taskIcons[task.kind] || ListChecks;
        return <button className="task-row" key={task.id} onClick={() => toggle(task.id)}><span className={`task-icon ${task.kind}`}><Icon /></span><span className="task-copy"><strong>{task.title}</strong><small>{task.subtitle}</small></span><span className={`due ${task.due_at ? '' : 'muted'}`}>{shortDate(task.due_at)}</span><span className="task-check"><Check /></span></button>;
      })}</div>
    </section>
    <section className="section-block favorite-block">
      <div className="section-heading"><h2>Ulubione</h2><button className="text-button" onClick={() => navigate('/oferty?favorite=true')}>Zobacz wszystkie <ArrowRight /></button></div>
      {data.favorites.length
        ? <div className="recent-list">{data.favorites.map(offer => <OfferCard key={offer.id} offer={offer} compact onClick={() => navigate(`/oferty/${offer.id}`)} onFavoriteToggle={toggleFavorite} />)}</div>
        : <div className="favorites-empty"><p>Nie masz jeszcze ulubionych ofert.</p><span>Kliknij serduszko przy ofercie, aby pojawiła się tutaj.</span></div>}
    </section>
    <section className="section-block recent-block">
      <div className="section-heading"><h2>Ostatnio dodane</h2><button className="text-button" onClick={() => navigate('/oferty')}>Zobacz wszystkie <ArrowRight /></button></div>
      <div className="recent-list">{data.offers.map(offer => <OfferCard key={offer.id} offer={offer} compact onClick={() => navigate(`/oferty/${offer.id}`)} onFavoriteToggle={toggleFavorite} />)}</div>
    </section>
    <section className="ai-bar"><button aria-label="Nagraj"><Mic /></button><input aria-label="Notatka dla asystenta" placeholder={data.aiEnabled ? 'Dodaj notatkę lub polecenie…' : 'Asystent AI — skonfiguruj klucz w ustawieniach'} /><button className="ai-send" aria-label="Wyślij"><Send /></button></section>
  </div>;
}
